import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Checkbox, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, OutlinedInput, TextField, Typography } from '@mui/material'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import ReductionScenarioService from '../../../shared/services/reduction-scenario/reduction-scenario.service'
import { IReductionScenario } from '../../../shared/services/reduction-scenario/reduction-scenario.model'

export const ReductionScenarioDialog: FunctionComponent<{
  open: boolean
  scenario?: IReductionScenario | null
  closeHandler: () => void
  successHandler: () => void
}> = ({ open, scenario, closeHandler, successHandler }) => {
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reductionScenarioService = new ReductionScenarioService()
  const isEdit = !!scenario

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [annualGrowthPercentage, setAnnualGrowthPercentage] = useState('')
  const [seedFromCurrentPlan, setSeedFromCurrentPlan] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setName(scenario?.name ?? '')
      setDescription(scenario?.description ?? '')
      setAnnualGrowthPercentage(scenario?.annualGrowthPercentage != null ? String(scenario.annualGrowthPercentage) : '')
      setSeedFromCurrentPlan(false)
    }
  }, [open, scenario])

  const handleSubmit = async () => {
    if (!name.trim()) return
    try {
      setIsSubmitting(true)
      const growth = annualGrowthPercentage.trim() === '' ? null : Number(annualGrowthPercentage)
      if (isEdit && scenario) {
        await reductionScenarioService.updateReductionScenario(scenario.id, { name: name.trim(), description: description || null, annualGrowthPercentage: growth })
      } else {
        await reductionScenarioService.createReductionScenario({ name: name.trim(), description: description || null, annualGrowthPercentage: growth, seedFromCurrentPlan })
      }
      successHandler()
      closeHandler()
    } catch (e: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: e?.response?.data?.message || 'Something went wrong',
        backgroundColor: 'text.primary',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title={isEdit ? 'Edit Scenario' : 'New Scenario'}
      cancelHandler={closeHandler}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!name.trim()}
      maxWidth='sm'
    >
      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='scenario-name'>Name</InputLabel>
        <OutlinedInput id='scenario-name' label='Name' value={name} onChange={(e) => setName(e.target.value)} />
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={2}
        label='Description'
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={{ mt: 2 }}
      />

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='scenario-growth'>Annual growth assumption (optional)</InputLabel>
        <OutlinedInput
          id='scenario-growth'
          label='Annual growth assumption (optional)'
          type='number'
          value={annualGrowthPercentage}
          onChange={(e) => setAnnualGrowthPercentage(e.target.value)}
          endAdornment={<InputAdornment position='end'><Typography variant='body2' sx={{ color: 'text.secondary' }}>%</Typography></InputAdornment>}
          inputProps={{ step: 0.1 }}
        />
        <FormHelperText>
          Grows this scenario's baseline by this much each year before its measures apply.
          Leave blank to use your plan's growth assumption. Use this instead of adding
          increase-emissions measures to model growth.
        </FormHelperText>
      </FormControl>

      {!isEdit && (
        <FormControl sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={seedFromCurrentPlan} onChange={(e) => setSeedFromCurrentPlan(e.target.checked)} />}
            label='Copy projects from current action plan'
          />
          <FormHelperText sx={{ mt: -0.5 }}>
            Starts the scenario as a snapshot of your existing planned reduction actions, which you can then adjust.
          </FormHelperText>
        </FormControl>
      )}
    </DialogWrapper>
  )
}
